import type { WorkflowStepPayload } from '@Application/Organizations/Workflows/Actions/organizationWorkflowsFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import WorkflowPromptsView from '@UI/Store/OrganizationWorkflows/Views/WorkflowPromptsView';
import WorkflowReevaluationView from '@UI/Store/OrganizationWorkflows/Views/WorkflowReevaluationView';
import WorkflowStepInputsView from '@UI/Store/OrganizationWorkflows/Views/WorkflowStepInputsView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * One document produced by a group. `stage` is zero-indexed; the type-specific blocks (`inputs`,
 * `prompts`, `reevaluation`) are null outside document generation.
 */
class WorkflowStepView {
	readonly id: number;
	readonly position: number;
	readonly stage: number;
	readonly type: LabeledValueView;
	readonly label: string;
	readonly documentTypeId: number | null;
	readonly usage: LabeledValueView | null;
	readonly inputs: WorkflowStepInputsView | null;
	readonly prompts: WorkflowPromptsView | null;
	readonly reevaluation: WorkflowReevaluationView | null;
	readonly splitCalls: boolean;
	readonly sectionDependencies: Readonly<Record<string, readonly string[]>>;
	readonly temperature: number | null;
	readonly vocabularyTerms: readonly string[] | null;
	readonly speakers: boolean | null;

	constructor(payload: WorkflowStepPayload) {
		this.id = payload.id;
		this.position = payload.position;
		this.stage = payload.stage;
		this.type = new LabeledValueView(payload.type);
		this.label = payload.label;
		this.documentTypeId = payload.documentType?.id ?? null;
		this.usage = payload.usage !== null ? new LabeledValueView(payload.usage) : null;
		this.inputs = payload.inputs !== null ? new WorkflowStepInputsView(payload.inputs) : null;
		this.prompts = payload.prompts !== null ? new WorkflowPromptsView(payload.prompts) : null;
		this.reevaluation =
			payload.reevaluation !== null ? new WorkflowReevaluationView(payload.reevaluation) : null;
		this.splitCalls = payload.splitCalls;
		this.sectionDependencies = payload.sectionDependencies;
		this.temperature = payload.temperature;
		this.vocabularyTerms = payload.vocabularyTerms;
		this.speakers = payload.speakers;

		Object.freeze(this);
	}
}

export default WorkflowStepView;
